import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { default as CustomLoadingOverlay } from "../../../../components/loaders/LazyLoading/LazyLoading";
import type { WorkOrderAttachment } from "@/pages/work-orders/models/WorkOrderAttachment";
import { AttachmentsDownloadButtons } from "./download-buttons/attachment-download-buttons.columns";
import { UploadAttachmentToWorkOrderDialog } from "./uploadAttachment-dialog/uploadAttachmentToWorkOrderDialog";
import { PreviewerAttachment } from "./previewer-attachment";
import { getWorkOrderAttachments } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import { get } from "lodash-es";
import { ErrorModal, useToken } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const useStyles = makeStyles((theme) => ({
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
}));

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	workOrderSelectedId: number | null;
	workOrderSelectedName: string | null;
	setSelectedRow?: Function;
	setAttachmentsLinked?: Function;
};

export const ViewerAttachmentsLinked = ({
	show,
	workOrderSelectedId,
	workOrderSelectedName,
	onHide,
	setSelectedRow,
	setAttachmentsLinked,
}: Props) => {
	const { token: bearerToken } = useToken();
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const classes = useStyles();
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [
		allWorkOrderAttachmentsRowsLoading,
		setAllWorkOrderAttachmentsRowsLoading,
	] = useState<boolean>(false);
	const [displayViewerAttachment, setDisplayViewerAttachment] =
		useState<boolean>(false);
	const [attachmentSelectedInTable, setAttachmentSelectedInTable] =
		useState<any>([]);
	const [error, setError] = useState<string>("");
	const [shouldUpdateWhenExit, setShouldUpdateWhenExit] = useState(false);
	const [isRemoveAttachmentLoading, setIsRemoveAttachmentLoading] =
		useState<boolean>(true);
	const [removeAttachmentError, setRemoveAttachmentError] =
		useState<string>("");
	const [attachmentsList, setAttachmentsList] = useState<WorkOrderAttachment[]>(
		[],
	);

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const handleCancel = () => {
		setSelectedRow && setSelectedRow([]);
		onHide(shouldUpdateWhenExit);
	};

	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

	const handleRowClicked = (ev: any) => {
		setAttachmentSelectedInTable(ev.data);
	};

	const loadWorkOrderAttachments = async () => {
		const res = await getWorkOrderAttachments(workOrderSelectedId as number);
		setAllWorkOrderAttachmentsRowsLoading(true);
		if (res.ok) {
			const rowsSource = get(res, "data.tables[0].rows", []);
			setAttachmentsList(rowsSource);
			setAttachmentsLinked && setAttachmentsLinked(rowsSource.length);
			setAllWorkOrderAttachmentsRowsLoading(false);
		} else {
			setAllWorkOrderAttachmentsRowsLoading(false);
			setError(res.message);
		}
	};
	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const onDobleClicked = () => {
		const selectedRow = gridApi?.getSelectedNodes();
		if (selectedRow) setAttachmentSelectedInTable(selectedRow[0].data);
		setDisplayViewerAttachment(true);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			setAttachmentSelectedInTable(data);

			const DownloadAttachment = () => {
				const link = document.createElement("a");
				link.href = `/backend/files/${data?.UploadFileId}/download?Authorization=${bearerToken}`;
				link.setAttribute("download", `${data?.Title} ${data?.FileName}`);
				document.body.appendChild(link);
				link.click();
			};

			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "View Attachment",
					action: () => {
						setAttachmentSelectedInTable(data);

						setDisplayViewerAttachment(true);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				},
			];

			if (data?.FileName) {
				result.push({
					name: "Download",
					action: () => {
						DownloadAttachment();
					},
					icon: '<i class="far fa-file-alt"></i>',
				});
			}

			return result;
		},
		[],
	);

	useEffect(() => {
		if (show) {
			setShouldUpdateWhenExit(false);
			loadWorkOrderAttachments();
			setColumnDefs([
				{
					field: "FileName",
					headerName: "File Name",
					flex: 8,
				},
				{
					minWidth: 55,
					maxWidth: 55,
					flex: 1,
					cellRenderer: (params: EditableCallbackParams) => (
						<Grid2
							container
							style={{ alignItems: "center", justifyContent: "center" }}
						>
							{params?.node.data.Path && (
								<Grid2 size={{ md: 12, xs: 12 }}>
									<IconButton
										onClick={() => {}}
										aria-label="delete"
										color="secondary"
										style={{
											height: "35px",
											width: "35px",
											justifyContent: "center",
											alignItems: "center",
											margin: "0 auto",
										}}
									>
										<DeleteIcon style={{ width: 35, height: 35 }} />
									</IconButton>
								</Grid2>
							)}
						</Grid2>
					),
				},
				{
					cellRenderer: AttachmentsDownloadButtons,
					field: "",
					flex: 2,
					minWidth: 250,
					maxWidth: 250,
				},
				{
					headerName: "",
					flex: 1,
					minWidth: 45,
					maxWidth: 45,
					cellClass: "ag-columns",
					wrapText: false,
					autoHeight: false,
					cellRenderer: (params: EditableCallbackParams) => {
						return (
							<Grid2
								container
								style={{
									height: "100%",
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									marginLeft: "-28px",
								}}
							>
								<Grid2
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
									size={{ xs: 12, md: 12 }}
								>
									<Grid2
										container
										spacing={1}
										style={{ justifyContent: "flex-end", minWidth: 30 }}
									>
										<Grid2 size={{ xs: 12, md: 12 }}>
											<Button
												fullWidth
												style={{}}
												onClick={(e) => onActionsClick(e, params)}
											>
												<FormatListBulletedSharpIcon
													style={{ height: "auto" }}
													color="action"
												/>
											</Button>
										</Grid2>
									</Grid2>
								</Grid2>
							</Grid2>
						);
					},
				},
			]);
		}
	}, [show]);

	return (
		<>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
				style={{ margin: "20px 25%" }}
			>
				<Grid2 container style={{ padding: "15px 0 0" }}>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<DialogTitle
							id="max-width-dialog-title"
							style={{ padding: "5px 24px 10px" }}
						>
							{" "}
							WORK ORDER: {workOrderSelectedName}
						</DialogTitle>
					</Grid2>
					<Grid2 container>
						<DialogContent
							dividers
							style={{ height: "35vh", paddingTop: "20px" }}
						>
							{allWorkOrderAttachmentsRowsLoading ? (
								<Grid2
									container
									style={{
										height: "30vh",
										width: "100%",
										justifyContent: "center",
										alignItems: "center",
										position: "relative",
									}}
								>
									<Grid2
										style={{
											position: "absolute",
											top: "30%",
											left: "50%",
											transform: "translate(-50%, -50%)",
										}}
										size={{ md: 12, xs: 12 }}
									>
										<LazyLoading />
									</Grid2>
								</Grid2>
							) : (
								<Grid2
									style={{
										height: "30vh",
										border: "4px solid #ccc",
										borderRadius: 10,
										margin: "0",
										padding: "0",
									}}
									size={{ md: 12, xs: 12 }}
								>
									<div style={gridStyle}>
										<AgGridReact
											onGridReady={(params) => setGridApi(params.api)}
											loadingOverlayComponent={loadingOverlayComponent}
											rowData={attachmentsList}
											getRowId={(params) => params.data.UploadFileId}
											onRowClicked={handleRowClicked}
											onRowDoubleClicked={onDobleClicked}
											getContextMenuItems={getContextMenuItems}
											columnDefs={columnDefs}
											rowHeight={45}
											rowSelection="single"
											headerHeight={32}
											animateRows={true}
											pagination={true}
											loading={allWorkOrderAttachmentsRowsLoading}
										/>
									</div>
								</Grid2>
							)}
							{displayViewerAttachment && (
								<PreviewerAttachment
									attachmentSelected={attachmentSelectedInTable}
									displayViewerAttachment={displayViewerAttachment}
									setDisplayViewerAttachment={setDisplayViewerAttachment}
								/>
							)}
						</DialogContent>
					</Grid2>
					<Grid2 container>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<DialogActions style={{ padding: "0", position: "relative" }}>
								<Grid2 size={{ md: 12, xs: 12 }}>
									<UploadAttachmentToWorkOrderDialog
										setError={setError}
										workOrderSelectedId={workOrderSelectedId}
										onHide={(shouldUpdate: boolean) => {
											loadWorkOrderAttachments();
											setShouldUpdateWhenExit(!!shouldUpdate);
										}}
									/>
								</Grid2>
								<Grid2
									container
									style={{
										margin: 0,
										padding: "2px 0 8px 0",
										position: "absolute",
										left: 20,
										bottom: 8,
									}}
									size={{ md: 5, xs: 6 }}
								>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={() => handleCancel()}
									>
										Close
									</Button>
								</Grid2>
							</DialogActions>
						</Grid2>
					</Grid2>
				</Grid2>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={removeAttachmentError}
				onHide={() => setRemoveAttachmentError("")}
			/>
		</>
	);
};
